import { z } from 'zod'

/**
 * Question model. One DAVINCI process is created per question (PLAN §3), so every question carries
 * its own ballot rules. The circuit has 16 fields ⇒ at most 16 options per question.
 */

export const MAX_OPTIONS = 16
export const MIN_OPTIONS = 2
/** Bound used for rating max values and quadratic budgets (keeps maxValue * maxVoters ≤ 1e12 with 100k voters). */
export const MAX_BALLOT_VALUE = 1_000_000

export const QuestionOptionSchema = z.object({
  title: z.string().trim().min(1, 'Option title is required').max(200),
})
export type QuestionOption = z.infer<typeof QuestionOptionSchema>

const base = {
  title: z.string().trim().min(1, 'Question title is required').max(300),
  description: z.string().trim().max(5000).default(''),
  options: z.array(QuestionOptionSchema).min(MIN_OPTIONS, `At least ${MIN_OPTIONS} options`).max(MAX_OPTIONS),
}

const posInt = z.number().int()

export const SingleChoiceConfigSchema = z.object({}).strict()
export const MultipleChoiceConfigSchema = z
  .object({ minSelections: posInt.min(0).default(0), maxSelections: posInt.min(1) })
  .strict()
export const ApprovalConfigSchema = z.object({}).strict()
export const RatingConfigSchema = z
  .object({ minValue: posInt.min(0).default(0), maxValue: posInt.min(1).max(MAX_BALLOT_VALUE) })
  .strict()
export const RankingConfigSchema = z.object({}).strict()
export const QuadraticConfigSchema = z.object({ budget: posInt.min(1).max(MAX_BALLOT_VALUE) }).strict()

export type MultipleChoiceConfig = z.infer<typeof MultipleChoiceConfigSchema>
export type RatingConfig = z.infer<typeof RatingConfigSchema>
export type QuadraticConfig = z.infer<typeof QuadraticConfigSchema>

/** Question as authored by the organizer (no ids). Discriminated on `type`. */
export const QuestionInputSchema = z
  .discriminatedUnion('type', [
    z.object({ ...base, type: z.literal('single_choice'), config: SingleChoiceConfigSchema.default({}) }),
    z.object({ ...base, type: z.literal('multiple_choice'), config: MultipleChoiceConfigSchema }),
    z.object({ ...base, type: z.literal('approval'), config: ApprovalConfigSchema.default({}) }),
    z.object({ ...base, type: z.literal('rating'), config: RatingConfigSchema }),
    z.object({ ...base, type: z.literal('ranking'), config: RankingConfigSchema.default({}) }),
    z.object({ ...base, type: z.literal('quadratic'), config: QuadraticConfigSchema }),
  ])
  .superRefine((q, ctx) => {
    const n = q.options.length
    if (q.type === 'multiple_choice') {
      if (q.config.maxSelections > n)
        ctx.addIssue({ code: 'custom', path: ['config', 'maxSelections'], message: `Cannot exceed ${n} options` })
      if (q.config.minSelections > q.config.maxSelections)
        ctx.addIssue({ code: 'custom', path: ['config', 'minSelections'], message: 'Cannot exceed max selections' })
    }
    if (q.type === 'rating' && q.config.maxValue <= q.config.minValue) {
      ctx.addIssue({ code: 'custom', path: ['config', 'maxValue'], message: 'Must be greater than the minimum' })
    }
  })
export type QuestionInput = z.infer<typeof QuestionInputSchema>
/** Input type before defaults are applied (what forms hold). */
export type QuestionInputDraft = z.input<typeof QuestionInputSchema>

/** Human labels for the UI. */
export const QUESTION_TYPE_LABELS: Record<QuestionInput['type'], { label: string; help: string }> = {
  single_choice: { label: 'Single choice', help: 'Voters pick exactly one option.' },
  multiple_choice: { label: 'Multiple choice', help: 'Voters pick between a minimum and a maximum number of options.' },
  approval: { label: 'Approval', help: 'Voters approve any number of options.' },
  rating: { label: 'Rating', help: 'Voters rate every option within a range (e.g. 0–5).' },
  ranking: { label: 'Ranking', help: 'Voters order all options from most to least preferred.' },
  quadratic: {
    label: 'Quadratic',
    help: 'Voters spend a credit budget; n votes on an option cost n² credits.',
  },
}

/** Sensible default config when the organizer switches a question's type. */
export const defaultQuestionConfig = (type: QuestionInput['type'], optionCount: number): QuestionInput['config'] => {
  switch (type) {
    case 'multiple_choice':
      return { minSelections: 0, maxSelections: Math.max(1, Math.min(2, optionCount)) }
    case 'rating':
      return { minValue: 0, maxValue: 5 }
    case 'quadratic':
      return { budget: 100 }
    default:
      return {}
  }
}
